import type { Phrase } from './copy.js';

/**
 * The two tile-and-baraha venues: Mahjong under the karport, and Pusoy Dos on
 * the garahe stage (`mj.pd.*`).
 *
 * Three vocabularies survive the crossing untranslated, and each for a reason:
 * chow, pong and kang are the English table's words too; Todas and Ambisyon are
 * the app's own named moments, which the design spec keeps in its English prose;
 * and a combo's Filipino name gives way to the English term of art where one
 * exists — apat na magkapareho is quads, bilog is balls, tapon is discard.
 */
export const MJ = {
  /* ── Mahjong · the felt ───────────────────────────────────────────────── */
  'mj.wall': { tl: 'Wall', en: 'Wall' },
  'mj.wall.aria': {
    tl: 'Wall, {n} tile ang natitira',
    en: 'Wall, {n} tiles left',
  },
  'mj.wall.aria.draw': {
    tl: 'Wall, {n} tile ang natitira — bumunot',
    en: 'Wall, {n} tiles left — draw',
  },
  'mj.hold.aria': { tl: '{who}, {n} tile sa kamay', en: '{who}, {n} tiles in hand' },
  'mj.pool.aria': { tl: 'Tapunan, {n} tile', en: 'Discards, {n} tiles' },
  'mj.shelf.aria': { tl: '{n} bulaklak sa shelf', en: '{n} flowers on the shelf' },

  /* ── Mahjong · your rack ──────────────────────────────────────────────── */
  'mj.rack.bare': { tl: 'Wala ka pang tile.', en: 'No tiles yet.' },
  'mj.rack.fresh.aria': { tl: '{tile}, kabubunot lang', en: '{tile}, just drawn' },

  /* ── Mahjong · the claim window ───────────────────────────────────────── */
  'mj.claim.time.aria': { tl: 'Natitirang oras sa claim', en: 'Time left to claim' },
  'mj.toast.claim': {
    tl: 'Nagtapon si {who} ng {tile} — sagutin mo.',
    en: '{who} discarded {tile} — answer it.',
  },

  /* ── Mahjong · the verbs ──────────────────────────────────────────────── */
  'mj.todas': { tl: 'Todas!', en: 'Clean sweep!' },
  'mj.act.kang': { tl: 'Kang', en: 'Kang' },
  'mj.act.kang.tile': { tl: 'Kang {idx}', en: 'Kang {idx}' },
  'mj.act.kang.aria': { tl: 'Kang, {tile}', en: 'Kang, {tile}' },
  'mj.act.pong': { tl: 'Pong', en: 'Pong' },
  'mj.act.chow': { tl: 'Chow {run}', en: 'Chow {run}' },
  'mj.act.chow.aria': { tl: 'Chow {tiles}', en: 'Chow {tiles}' },
  'mj.act.pass': { tl: 'Pass', en: 'Pass' },
  'mj.act.draw': { tl: 'Bunot', en: 'Draw' },
  'mj.act.pick': { tl: 'Pumili', en: 'Pick' },
  'mj.act.discard': { tl: 'Tapon', en: 'Discard' },

  /* ── Mahjong · the numeral-index toggle ───────────────────────────────── */
  'mj.numerals': { tl: 'Numero', en: 'Numbers' },
  'mj.numerals.aria': { tl: 'Mga numero sa gilid ng tile', en: 'Corner numbers on tiles' },
  'mj.numerals.on': { tl: 'bukas', en: 'on' },
  'mj.numerals.off': { tl: 'sara', en: 'off' },

  /* ── Mahjong · the end of a hand ──────────────────────────────────────── */
  'mj.todas.self': { tl: '{who} — bunot mismo.', en: '{who} — self-draw.' },
  'mj.todas.fed': { tl: '{who} — sagasa kay {from}.', en: '{who} — {from} fed it.' },
  'mj.todas.siete': { tl: 'Siete pares.', en: 'Seven pairs.' },
  'mj.todas.total': { tl: 'Ambisyon', en: 'Total' },
  'mj.over.washout': { tl: 'Sabaw', en: 'Washout' },
  'mj.over.title': { tl: 'Tapos ang hand', en: 'Hand over' },
  'mj.over.washout.sub': {
    tl: 'Ubos na ang wall at walang umuwi — tabla, balik ang buy-in.',
    en: 'Wall ran dry and nobody went home — a draw, buy-ins back.',
  },
  'mj.over.quit': { tl: 'Umalis si {who}. Void ang hand.', en: '{who} left. The hand is void.' },
  'mj.over.none': { tl: 'Walang nanalo sa hand na ito.', en: 'Nobody won this hand.' },

  /* ── Mahjong · melds ──────────────────────────────────────────────────── */
  'mj.meld.aria': { tl: '{kind} {tile}', en: '{kind} {tile}' },
  'mj.meld.aria.closed': { tl: '{kind} {tile}, sarado', en: '{kind} {tile}, concealed' },
  'mj.kind.chow': { tl: 'chow', en: 'chow' },
  'mj.kind.pong': { tl: 'pong', en: 'pong' },
  'mj.kind.kang': { tl: 'kang', en: 'kang' },

  /* ── Mahjong · tile names, spoken by the screen reader ────────────────── */
  'mj.tile.dots': { tl: 'bilog {v}', en: '{v} balls' },
  'mj.tile.bamboo': { tl: 'kawayan {v}', en: '{v} bamboo' },
  'mj.tile.chars': { tl: 'caracteres {v}', en: '{v} characters' },
  'mj.tile.flower': { tl: 'bulaklak {v}', en: 'flower {v}' },
  'mj.tile.wind.E': { tl: 'hangin silangan', en: 'east wind' },
  'mj.tile.wind.S': { tl: 'hangin timog', en: 'south wind' },
  'mj.tile.wind.W': { tl: 'hangin kanluran', en: 'west wind' },
  'mj.tile.wind.N': { tl: 'hangin hilaga', en: 'north wind' },
  'mj.tile.dragon.red': { tl: 'dragon pula', en: 'red dragon' },
  'mj.tile.dragon.green': { tl: 'dragon berde', en: 'green dragon' },
  'mj.tile.dragon.white': { tl: 'dragon puti', en: 'white dragon' },

  /* ── Mahjong · the one line above the rail ────────────────────────────── */
  'mj.turn.over': { tl: 'Tapos na ang hand.', en: 'The hand is over.' },
  'mj.turn.wait': { tl: 'Naghihintay…', en: 'Waiting…' },
  'mj.turn.you.claim': { tl: 'Ikaw — sagutin ang tapon.', en: 'You — answer the discard.' },
  'mj.turn.claim': {
    tl: 'Sinasagot ni {who} ang tapon.',
    en: '{who} is answering the discard.',
  },
  'mj.turn.other': { tl: 'Tira ni {who}.', en: '{who}’s turn.' },
  'mj.turn.you.draw': { tl: 'Ikaw — bumunot ka sa wall.', en: 'You — draw from the wall.' },
  'mj.turn.you.act': { tl: 'Ikaw — pumili ng itatapon.', en: 'You — pick a discard.' },

  /* ── Pusoy Dos · the felt ─────────────────────────────────────────────── */
  'mj.pd.cards': { tl: '{n} baraha', en: '{n} cards' },
  'mj.pd.quit': { tl: 'alis', en: 'quit' },
  'mj.pd.place': { tl: 'ika-{n}', en: '#{n}' },
  'mj.pd.passed': { tl: 'pass', en: 'pass' },

  /* ── Pusoy Dos · your hand ────────────────────────────────────────────── */
  'mj.pd.bare': { tl: 'Ubos na ang kamay mo.', en: 'You are out of cards.' },
  'mj.pd.card': { tl: '{rank} ng {suit}', en: '{rank} of {suit}' },
  'mj.pd.suit.S': { tl: 'espada', en: 'spades' },
  'mj.pd.suit.H': { tl: 'puso', en: 'hearts' },
  'mj.pd.suit.D': { tl: 'diamante', en: 'diamonds' },
  'mj.pd.suit.C': { tl: 'trebol', en: 'clubs' },

  /* ── Pusoy Dos · the verbs and the pick line ──────────────────────────── */
  'mj.pd.play': { tl: 'Tira na', en: 'Play' },
  'mj.pd.pick.open': { tl: 'Pumili — kasama ang 3♣.', en: 'Pick — include the 3♣.' },
  'mj.pd.pick': { tl: 'Pumili ng baraha.', en: 'Pick your cards.' },
  'mj.pd.pick.bad': { tl: 'Hindi ’yan tira.', en: 'That’s not a play.' },
  'mj.pd.pick.bomba': { tl: '{combo} — bomba!', en: '{combo} — bomb!' },
  'mj.pd.pick.needthree': { tl: 'Kulang sa 3♣.', en: 'Needs the 3♣.' },
  'mj.pd.pick.weak': { tl: 'Hindi tinatalo ang mesa.', en: 'Does not beat the table.' },
  'mj.pd.clear': { tl: 'bawiin', en: 'clear' },

  /* ── Pusoy Dos · what a pick is worth ─────────────────────────────────── */
  'mj.pd.combo.single': { tl: 'isahan', en: 'single' },
  'mj.pd.combo.pair': { tl: 'pares', en: 'pair' },
  'mj.pd.combo.trip': { tl: 'tatluhan', en: 'trip' },
  'mj.pd.combo.five': { tl: 'limahan', en: 'five' },
  'mj.pd.combo.straight': { tl: 'straight', en: 'straight' },
  'mj.pd.combo.flush': { tl: 'flush', en: 'flush' },
  'mj.pd.combo.fullhouse': { tl: 'full house', en: 'full house' },
  'mj.pd.combo.quads': { tl: 'apat na magkapareho', en: 'quads' },
  'mj.pd.combo.straightflush': { tl: 'straight flush', en: 'straight flush' },

  /* ── Pusoy Dos · the one line above the rail ──────────────────────────── */
  'mj.pd.toast.nopot': {
    tl: 'Tapos na ang hand — walang kumuha ng pot.',
    en: 'The hand is over — nobody took the pot.',
  },
  'mj.pd.toast.won': {
    tl: 'Ubos na si {who} — tapos na ang hand.',
    en: '{who} ran out — the hand is over.',
  },
  'mj.pd.toast.open.you': {
    tl: 'Bukas ang mesa — ikaw ang mag-lead.',
    en: 'Table is open — you lead.',
  },
  'mj.pd.toast.open': {
    tl: 'Bukas ang mesa — {who} ang mag-lead.',
    en: 'Table is open — {who} leads.',
  },
  'mj.pd.toast.yours': {
    tl: 'Tira mo: {combo} — hawak mo ang mesa.',
    en: 'Your play: {combo} — you hold the table.',
  },
  'mj.pd.toast.beat': {
    tl: 'Tira ni {who}: {combo} — talunin o pass.',
    en: '{who} played {combo} — beat it or pass.',
  },
  'mj.pd.toast.next': {
    tl: 'Tira ni {who}: {combo} — naghihintay ng susunod.',
    en: '{who} played {combo} — waiting on the next.',
  },
  'mj.pd.toast.waiting': {
    tl: 'Tira ni {who}: {combo} — hinihintay si {next}.',
    en: '{who} played {combo} — waiting on {next}.',
  },
  /* Singulars: English inflects after a count and Taglish does not. */
  'mj.wall.aria.one': { tl: 'Wall, {n} tile ang natitira', en: 'Wall, {n} tile left' },
  'mj.hold.aria.one': { tl: '{who}, {n} tile sa kamay', en: '{who}, {n} tile in hand' },
  'mj.pool.aria.one': { tl: 'Tapunan, {n} tile', en: 'Discards, {n} tile' },
  'mj.shelf.aria.one': { tl: '{n} bulaklak sa shelf', en: '{n} flower on the shelf' },
  'mj.pd.cards.one': { tl: '{n} baraha', en: '{n} card' },
} as const satisfies Record<string, Phrase>;
